import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./css/FrontPage.css";
import "./css/semantic.css";
import { jwtDecode } from "jwt-decode";
import { fetchNewsdata } from "./services/newsdata";
import ArticleCard from "./ArticleCard";
import ColoredLine from "./SmallComponents/ColoredLine";
import { Link, useLocation } from "react-router-dom";

const apiKey = process.env.REACT_APP_NEWSDATA_API_KEY;

function parseTopics(pref) {
  if (pref == null || pref === "") return [];
  return String(pref)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
console.log("topics", parseTopics(localStorage.getItem("preferences")));

/** Normalized title for dedupe, or null when title is missing/blank (those rows are not deduped). */
function articleTitleDedupeKey(article) {
  const raw = article?.title;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s === "" ? null : s.toLowerCase();
}

/** Dedupes the combined feed by title, keeping the first occurrence. */
function dedupeArticlesByTitle(articles) {
  const seen = new Set();
  return articles.filter((article) => {
    const key = articleTitleDedupeKey(article);
    if (key == null) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const FrontPage = () => {
  const username = localStorage.getItem("username");
  const location = useLocation();
  const selectedTopics = location.state?.topics ?? localStorage.getItem("preferences") ?? "";
  const selectedTerm = localStorage.getItem("freePreferences") ?? "";

  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [dateTime, setDateTime] = useState(new Date());
  const [article, setArticle] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [topicErrors, setTopicErrors] = useState([]);
  const [loading, setLoading] = useState(false);


  const date = dateTime.toLocaleDateString();

  const checkToken = useCallback(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setUserLoggedIn(false);
      return;
    }
    try {
      jwtDecode(token);
      setUserLoggedIn(true);
    } catch {
      setUserLoggedIn(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setDateTime(new Date());
    checkToken();

    const topics = parseTopics(selectedTopics);
    const rawTerm = selectedTerm;
    // Older preference saves stored a missing value as the string "null".
    const term = rawTerm == null || rawTerm === "null" ? "" : rawTerm.trim();

    async function fetchTopic(topic, url) {
      if (!active) return;
      try {
        const response = await fetchNewsdata(url, () => active);
        if (response?.data?.error) throw new Error(response.data.error.info || "Request failed.");
        if (active) setArticle(previous => [...previous, ...(response?.data?.data ?? [])]);
      } catch (error) {
        if (active) setTopicErrors(previous => [...previous, `${topic} (${error.message || "Request failed"})`]);
      }
    }
    console.log("topics", topics)
    const getApi = async () => {
      setTopicErrors([]);

      if (topics.includes("Australia")) {
        await fetchTopic("Australia",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&language=en&country=au`);
      }
      if (topics.includes("Asia")) {
        await fetchTopic("Asia",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&language=en&country=cn,hk,jp,ph,kr`);
      }
      if (topics.includes("U.K.")) {
        await fetchTopic("U.K.",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&language=en&country=gb`);
      }
      if (topics.includes("US")) {
        await fetchTopic("US",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&language=en&country=us`);
      }
      if (topics.includes("Business")) {
        await fetchTopic("Business",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&language=en&category=business`);
      }
      if (topics.includes("Entertainment")) {
        await fetchTopic("Entertainment",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&category=entertainment&language=en`);
      }
      if (topics.includes("Food")) {
        await fetchTopic("Food",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&category=food&language=en`);
      }
      if (topics.includes("General")) {
        await fetchTopic("General",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&language=en`);
      }
      if (topics.includes("Health")) {
        await fetchTopic("Health",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&category=health&language=en`);
      }
      if (topics.includes("Science")) {
        await fetchTopic("Science",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&category=science&language=en`);
      }
      if (topics.includes("Sports")) {
        await fetchTopic("Sports",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&category=sports&language=en`);
      }
      if (topics.includes("Technology")) {
        await fetchTopic("Technology",
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&category=technology&language=en`);
      }
    };

    const getApi2 = async () => {
      try {
        const q = encodeURIComponent(term);
        const res11 = await fetchNewsdata(
          `https://newsdata.io/api/1/latest?apikey=${apiKey}&q=${q}&language=en`,
          () => active
        );
        if (res11?.data?.error) {
          throw new Error(res11.data.error.info || "Search request failed.");
        }
        if (active) setArticle(previous => [...previous, ...(res11?.data?.data ?? [])]);
      } catch (error) {
        if (!active) return;
        setSearchError(
          error.message || "Search request failed."
        );
      }
    };

    setArticle([]);
    setSearchError("");
    setLoading(true);
    Promise.all([getApi(), ...(term !== "" ? [getApi2()] : [])]).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [checkToken, selectedTopics, selectedTerm]);

  const displayArticles = useMemo(() => dedupeArticlesByTitle(article), [article]);

//   if (!userLoggedIn || username === null) {
//     return <Navigate to="/" />;
//   }

  return (
    <main className="front-page">
      <div className="front-page__inner">
        <nav className="links" aria-label="Main">
          <Link to="/">Hi {username},</Link>
          <Link to="/users">Preferences</Link>
          <Link to="/users/frontpage">FrontPage</Link>
          <Link to="/users/forum">Forum</Link>
          <Link to="/users/archives">Archives</Link>
          <Link to="/logout">Logout</Link>
        </nav>

        <header className="front-page__header">
          <h1 className="front-page__title title">Your Front Page News</h1>
          <h2 className="front-page__date date">{date}</h2>
        </header>

        <div className="front-page__rule">
          <ColoredLine color="black" />
        </div>

        {loading && <p role="status">Loading articles gradually. More articles will appear as they arrive.</p>}
        {topicErrors.length > 0 && <p role="alert">Unable to load articles for: {topicErrors.join(", ")}. Please try again shortly.</p>}
        {searchError && <p role="alert">Unable to load search articles: {searchError}</p>}
        <section className="front-page__articles">
          {displayArticles.map((c, i) => (
            <ArticleCard
              title={c.title}
              key={`${c.url || "article"}-${i}`}
              url={c.url}
              description={c.description}
              image={c.image}
              author={c.author}
            />
          ))}
        </section>

        <footer className="front-page__footer">
          <h2 className="closing">
            <Link to="/users">Go back to Preferences Page</Link>
          </h2>
        </footer>
      </div>
    </main>
  );
};

export default FrontPage;
